import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API, useAuth } from '@/App';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  User, Upload, FileText, Phone, Linkedin, Globe, Plus, X,
  CheckCircle, Loader2, GraduationCap, BookOpen, Award, Video,
  Building2, Briefcase, FolderOpen, Sparkles, Save, MapPin, Calendar, Tag, Trash2,
} from 'lucide-react';

const SECTIONS = [
  { id: 'basic', label: 'Basic Detail', icon: User, weight: 15 },
  { id: 'personal', label: 'Personal Details', icon: MapPin, weight: 10 },
  { id: 'resume', label: 'Resume', icon: FileText, weight: 15 },
  { id: 'video', label: 'Video Resume', icon: Video, weight: 5 },
  { id: 'industry', label: 'Industry & Skills', icon: Tag, weight: 15 },
  { id: 'education', label: 'Education', icon: GraduationCap, weight: 10 },
  { id: 'projects', label: 'Projects', icon: FolderOpen, weight: 5 },
  { id: 'certifications', label: 'Certifications', icon: Award, weight: 10 },
  { id: 'experience', label: 'Experience', icon: Briefcase, weight: 15 },
];

const CandidateProfilePage = () => {
  const { user, token } = useAuth();
  const fileInputRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [constants, setConstants] = useState(null);
  const [activeSection, setActiveSection] = useState('basic');

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', linkedin: '', portfolio: '',
    gender: '', date_of_birth: '', country: '', state: '', city: '', address: '',
    skills: [], industry_tags: [], subjects_can_teach: [], grade_levels_experience: [], boards_experience: [],
    teaching_experience_years: '', current_school: '', current_designation: '', teaching_philosophy: '',
    education: [], projects: [], certifications: [], work_experience: [],
    video_resume_url: '', demo_video_url: '',
  });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    (async () => {
      try {
        const [profileRes, constantsRes] = await Promise.all([
          axios.get(`${API}/educator/profile`, { headers, withCredentials: true }),
          axios.get(`${API}/education/constants`),
        ]);
        const p = profileRes.data;
        setProfile(p);
        setConstants(constantsRes.data);
        setForm({
          first_name: p.first_name || (user?.name?.split(' ')[0] || ''),
          last_name: p.last_name || (user?.name?.split(' ').slice(1).join(' ') || ''),
          email: p.email || user?.email || '',
          phone: p.phone || '', linkedin: p.linkedin || '', portfolio: p.portfolio || '',
          gender: p.gender || '', date_of_birth: p.date_of_birth || '',
          country: p.country || '', state: p.state || '', city: p.city || '', address: p.address || '',
          skills: p.skills || [], industry_tags: p.industry_tags || [],
          subjects_can_teach: p.subjects_can_teach || [],
          grade_levels_experience: p.grade_levels_experience || [],
          boards_experience: p.boards_experience || [],
          teaching_experience_years: p.teaching_experience_years?.toString() || '',
          current_school: p.current_school || '', current_designation: p.current_designation || '',
          teaching_philosophy: p.teaching_philosophy || '',
          education: p.education || [], projects: p.projects || [],
          certifications: p.certifications || [], work_experience: p.work_experience || [],
          video_resume_url: p.video_resume_url || '', demo_video_url: p.demo_video_url || '',
        });
      } catch { toast.error('Failed to load profile'); }
      setLoading(false);
    })();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, teaching_experience_years: form.teaching_experience_years ? parseInt(form.teaching_experience_years) : null };
      const res = await axios.put(`${API}/educator/profile`, payload, { headers, withCredentials: true });
      setProfile(res.data);
      toast.success('Profile saved!');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'].includes(file.type)) {
      toast.error('Please upload PDF, DOCX, or TXT'); return;
    }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API}/educator/resume/upload`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }, withCredentials: true,
      });
      const parsed = res.data.parsed_data || {};
      const prof = res.data.profile || {};
      setProfile(prof);
      setForm(f => ({
        ...f,
        first_name: parsed.first_name || f.first_name,
        last_name: parsed.last_name || f.last_name,
        email: parsed.email || f.email,
        phone: parsed.phone || f.phone,
        city: parsed.city || f.city,
        state: parsed.state || f.state,
        skills: parsed.skills?.length ? parsed.skills : f.skills,
        industry_tags: parsed.industry_tags?.length ? parsed.industry_tags : f.industry_tags,
        subjects_can_teach: parsed.subjects?.length ? parsed.subjects : f.subjects_can_teach,
        education: parsed.education?.length ? parsed.education : f.education,
        work_experience: parsed.work_experience?.length ? parsed.work_experience : f.work_experience,
        certifications: parsed.certifications?.length ? parsed.certifications : f.certifications,
        projects: parsed.projects?.length ? parsed.projects : f.projects,
        teaching_experience_years: parsed.experience_years?.toString() || f.teaching_experience_years,
      }));
      toast.success('Resume parsed! Profile fields auto-filled.');
    } catch { toast.error('Failed to upload resume'); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const updateField = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const toggleTag = (field, value) => {
    setForm(f => {
      const arr = f[field] || [];
      return { ...f, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  const addListEntry = (field, entry) => setForm(f => ({ ...f, [field]: [...(f[field] || []), entry] }));
  const removeListEntry = (field, idx) => setForm(f => ({ ...f, [field]: f[field].filter((_, i) => i !== idx) }));
  const updateListEntry = (field, idx, key, value) => {
    setForm(f => {
      const list = [...f[field]]; list[idx] = { ...list[idx], [key]: value };
      return { ...f, [field]: list };
    });
  };

  const completion = profile?.profile_completion || 0;

  const sectionFilled = (id) => {
    switch (id) {
      case 'basic': return !!(form.first_name && form.phone);
      case 'personal': return !!(form.gender || form.city);
      case 'resume': return !!(profile?.resume_text);
      case 'video': return !!(form.video_resume_url);
      case 'industry': return !!(form.skills.length || form.industry_tags.length);
      case 'education': return form.education.length > 0;
      case 'projects': return form.projects.length > 0;
      case 'certifications': return form.certifications.length > 0;
      case 'experience': return form.work_experience.length > 0;
      default: return false;
    }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto animate-fadeIn" data-testid="candidate-profile-page">
        {/* Auto-fill Banner */}
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-xl p-5 mb-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-white" data-testid="autofill-banner">
          <div className="flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-indigo-200" />
            <div>
              <p className="font-semibold text-lg">Auto-fill Your Profile</p>
              <p className="text-indigo-100 text-sm">Upload your resume and we'll extract your experience, skills, and education</p>
            </div>
          </div>
          <input ref={fileInputRef} type="file" accept=".pdf,.docx,.txt" onChange={handleFileUpload} className="hidden" />
          <Button className="bg-white text-indigo-700 hover:bg-indigo-50 shrink-0" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-resume-btn">
            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Parsing...</> : <><Upload className="w-4 h-4 mr-2" />Upload Resume</>}
          </Button>
        </div>

        <div className="grid lg:grid-cols-[220px_1fr] gap-6">
          {/* Sidebar */}
          <div className="space-y-2" data-testid="profile-sidebar">
            {/* Completion */}
            <Card className="p-4 mb-3">
              <p className="text-xs font-medium text-slate-500 uppercase mb-2">Profile Completion</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-slate-100 rounded-full h-2.5">
                  <div className="bg-indigo-600 rounded-full h-2.5 transition-all" style={{ width: `${completion}%` }} />
                </div>
                <span className="text-sm font-bold text-slate-700" data-testid="profile-completion">{completion}%</span>
              </div>
            </Card>
            {SECTIONS.map(s => (
              <button
                key={s.id}
                onClick={() => setActiveSection(s.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${activeSection === s.id ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'text-slate-600 hover:bg-slate-50'}`}
                data-testid={`section-${s.id}`}
              >
                <s.icon className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1">{s.label}</span>
                {sectionFilled(s.id) ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <Badge className="bg-slate-100 text-slate-500 text-xs px-1.5 py-0">+{s.weight}%</Badge>
                )}
              </button>
            ))}
            <Button className="w-full mt-3" onClick={handleSave} disabled={saving} data-testid="save-profile-btn">
              <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>

          {/* Content Area */}
          <div className="space-y-6">
            {activeSection === 'basic' && <BasicDetailSection form={form} updateField={updateField} user={user} />}
            {activeSection === 'personal' && <PersonalDetailsSection form={form} updateField={updateField} />}
            {activeSection === 'resume' && <ResumeSection profile={profile} fileInputRef={fileInputRef} uploading={uploading} handleFileUpload={handleFileUpload} token={token} />}
            {activeSection === 'video' && <VideoResumeSection form={form} updateField={updateField} headers={headers} profile={profile} setProfile={setProfile} />}
            {activeSection === 'industry' && <IndustrySection form={form} updateField={updateField} toggleTag={toggleTag} constants={constants} />}
            {activeSection === 'education' && <EducationSection form={form} addListEntry={addListEntry} removeListEntry={removeListEntry} updateListEntry={updateListEntry} />}
            {activeSection === 'projects' && <ProjectsSection form={form} addListEntry={addListEntry} removeListEntry={removeListEntry} updateListEntry={updateListEntry} />}
            {activeSection === 'certifications' && <CertificationsSection form={form} addListEntry={addListEntry} removeListEntry={removeListEntry} updateListEntry={updateListEntry} />}
            {activeSection === 'experience' && <ExperienceSection form={form} addListEntry={addListEntry} removeListEntry={removeListEntry} updateListEntry={updateListEntry} />}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

// ===== SECTION COMPONENTS =====

const BasicDetailSection = ({ form, updateField, user }) => (
  <Card data-testid="section-basic-content">
    <CardHeader><CardTitle className="flex items-center gap-2"><User className="w-5 h-5 text-indigo-600" />Basic Detail</CardTitle></CardHeader>
    <CardContent className="space-y-4">
      <div className="flex items-center gap-4 mb-4">
        <div className="w-16 h-16 bg-indigo-100 rounded-xl flex items-center justify-center">
          {user?.picture ? <img src={user.picture} alt="" className="w-16 h-16 rounded-xl object-cover" /> : <User className="w-8 h-8 text-indigo-600" />}
        </div>
        <div><p className="font-semibold text-slate-900">{form.first_name} {form.last_name}</p><p className="text-sm text-slate-500">{form.email}</p></div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div><Label>First Name *</Label><Input value={form.first_name} onChange={(e) => updateField('first_name', e.target.value)} className="mt-1" data-testid="input-first-name" /></div>
        <div><Label>Last Name *</Label><Input value={form.last_name} onChange={(e) => updateField('last_name', e.target.value)} className="mt-1" data-testid="input-last-name" /></div>
        <div><Label>Email</Label><Input value={form.email} onChange={(e) => updateField('email', e.target.value)} className="mt-1" data-testid="input-email" /></div>
        <div><Label>Phone *</Label><Input value={form.phone} onChange={(e) => updateField('phone', e.target.value)} placeholder="+91 98765 43210" className="mt-1" data-testid="input-phone" /></div>
        <div><Label>LinkedIn</Label><Input value={form.linkedin} onChange={(e) => updateField('linkedin', e.target.value)} placeholder="https://linkedin.com/in/..." className="mt-1" /></div>
        <div><Label>Portfolio / Website</Label><Input value={form.portfolio} onChange={(e) => updateField('portfolio', e.target.value)} placeholder="https://..." className="mt-1" /></div>
      </div>
    </CardContent>
  </Card>
);

const PersonalDetailsSection = ({ form, updateField }) => (
  <Card data-testid="section-personal-content">
    <CardHeader><CardTitle className="flex items-center gap-2"><MapPin className="w-5 h-5 text-indigo-600" />Personal Details</CardTitle></CardHeader>
    <CardContent className="space-y-4">
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <Label>Gender</Label>
          <Select value={form.gender} onValueChange={(v) => updateField('gender', v)}>
            <SelectTrigger className="mt-1" data-testid="input-gender"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
              <SelectItem value="other">Other</SelectItem>
              <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Date of Birth</Label><Input type="date" value={form.date_of_birth} onChange={(e) => updateField('date_of_birth', e.target.value)} className="mt-1" data-testid="input-dob" /></div>
        <div><Label>Country</Label><Input value={form.country} onChange={(e) => updateField('country', e.target.value)} placeholder="India" className="mt-1" /></div>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div><Label>Province / State</Label><Input value={form.state} onChange={(e) => updateField('state', e.target.value)} className="mt-1" /></div>
        <div><Label>City</Label><Input value={form.city} onChange={(e) => updateField('city', e.target.value)} className="mt-1" data-testid="input-city" /></div>
        <div><Label>Address</Label><Input value={form.address} onChange={(e) => updateField('address', e.target.value)} className="mt-1" /></div>
      </div>
    </CardContent>
  </Card>
);

const ResumeSection = ({ profile, fileInputRef, uploading, handleFileUpload, token }) => {
  const hasResume = profile?.resume_text || profile?.resume_url;
  const baseUrl = (() => {
    if (!profile?.resume_url) return null;
    if (profile.resume_url.startsWith('http')) return profile.resume_url;
    return `${API.replace('/api', '')}${profile.resume_url}`;
  })();
  const resumeHref = baseUrl && token ? `${baseUrl}?token=${encodeURIComponent(token)}` : baseUrl;
  return (
    <Card data-testid="section-resume-content">
      <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-indigo-600" />Resume</CardTitle></CardHeader>
      <CardContent>
        <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
          <input ref={fileInputRef} type="file" accept=".pdf,.docx,.txt" onChange={handleFileUpload} className="hidden" id="resume-upload" />
          {uploading ? (
            <div className="space-y-3"><Loader2 className="w-12 h-12 text-indigo-600 mx-auto animate-spin" /><p className="text-slate-600">AI is parsing your resume...</p></div>
          ) : hasResume ? (
            <div className="space-y-3">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto" />
              <p className="text-slate-900 font-medium">Resume on file</p>
              {profile?.resume_filename && <p className="text-sm text-slate-500">{profile.resume_filename}</p>}
              <p className="text-xs text-slate-400 max-w-md mx-auto">Only one resume can be stored at a time. Uploading a new file will replace this one.</p>
              <div className="flex flex-wrap justify-center gap-2 pt-1">
                {resumeHref && (
                  <a href={resumeHref} target="_blank" rel="noopener noreferrer" data-testid="view-resume-btn">
                    <Button variant="outline"><FileText className="w-4 h-4 mr-2" />View Resume</Button>
                  </a>
                )}
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="replace-resume-btn">
                  <Upload className="w-4 h-4 mr-2" />Replace Resume
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <FileText className="w-12 h-12 text-slate-300 mx-auto" />
              <p className="text-slate-900 font-medium">Upload your resume</p>
              <p className="text-sm text-slate-500">AI will extract your qualifications, skills & experience</p>
              <Button onClick={() => fileInputRef.current?.click()} data-testid="select-resume-btn"><Upload className="w-4 h-4 mr-2" />Select File</Button>
            </div>
          )}
        </div>
        <p className="text-xs text-slate-400 mt-3 text-center">Supported: PDF, DOCX, TXT (max 5MB)</p>
      </CardContent>
    </Card>
  );
};


const VideoResumeSection = ({ form, updateField, headers, profile, setProfile }) => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const videoInputRef = React.useRef(null);

  const handleVideoUpload = async (e, videoType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
    if (!allowed.includes(file.type)) {
      toast.error('Please upload MP4, WebM, MOV, or AVI video'); return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Video must be under 100MB'); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/educator/video/upload?video_type=${videoType}`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
        timeout: 120000,
      });
      updateField(videoType === 'resume' ? 'video_resume_url' : 'demo_video_url', res.data.video_url);
      toast.success('Video uploaded successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload video');
    }
    setUploading(false);
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const handleDeleteVideo = async (videoType) => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/educator/video/${videoType}`, { headers, withCredentials: true });
      updateField(videoType === 'resume' ? 'video_resume_url' : 'demo_video_url', '');
      toast.success('Video removed');
    } catch {
      toast.error('Failed to remove video');
    }
    setDeleting(false);
  };

  const renderVideoBlock = (type, label, url) => (
    <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center space-y-3" data-testid={`video-${type}-block`}>
      <input
        ref={type === 'resume' ? videoInputRef : undefined}
        type="file"
        accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
        onChange={(e) => handleVideoUpload(e, type)}
        className="hidden"
        id={`video-${type}-input`}
        data-testid={`video-${type}-input`}
      />
      {uploading ? (
        <div className="space-y-2">
          <Loader2 className="w-10 h-10 text-indigo-600 mx-auto animate-spin" />
          <p className="text-slate-600 text-sm">Uploading video...</p>
        </div>
      ) : url ? (
        <div className="space-y-3">
          <video
            src={`${API.replace('/api', '')}${url}`}
            controls
            preload="metadata"
            playsInline
            className="w-full max-w-md mx-auto rounded-lg border"
            style={{ maxHeight: 280 }}
            data-testid={`video-${type}-player`}
          />
          <p className="text-sm text-green-600 font-medium flex items-center justify-center gap-1">
            <CheckCircle className="w-4 h-4" /> {label} uploaded
          </p>
          <div className="flex justify-center gap-2">
            <Button variant="outline" size="sm" onClick={() => document.getElementById(`video-${type}-input`).click()}>
              <Upload className="w-3.5 h-3.5 mr-1.5" /> Replace
            </Button>
            <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteVideo(type)} disabled={deleting} data-testid={`video-${type}-delete`}>
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <Video className="w-10 h-10 text-slate-300 mx-auto" />
          <p className="text-slate-900 font-medium text-sm">{label}</p>
          <p className="text-xs text-slate-500">Upload a short video introduction (MP4, WebM, MOV — max 100MB)</p>
          <Button variant="outline" onClick={() => document.getElementById(`video-${type}-input`).click()} data-testid={`video-${type}-upload-btn`}>
            <Upload className="w-4 h-4 mr-2" /> Upload Video
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Card data-testid="section-video-content">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Video className="w-5 h-5 text-indigo-600" /> Video Resume
        </CardTitle>
        <p className="text-sm text-slate-500 mt-1">
          Add a video introduction to stand out. Recruiters are 3x more likely to shortlist candidates with video profiles.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {renderVideoBlock('resume', 'Video Introduction', form.video_resume_url)}
        <div className="border-t pt-6">
          <h4 className="text-sm font-medium text-slate-700 mb-3">Demo Teaching Video (Optional)</h4>
          {renderVideoBlock('demo', 'Demo Lesson Video', form.demo_video_url)}
        </div>
      </CardContent>
    </Card>
  );
};


const IndustrySection = ({ form, updateField, toggleTag, constants }) => {
  const [tagInput, setTagInput] = useState('');
  const addTag = (field) => {
    if (tagInput.trim() && !(form[field] || []).includes(tagInput.trim())) {
      updateField(field, [...(form[field] || []), tagInput.trim()]);
      setTagInput('');
    }
  };
  return (
    <Card data-testid="section-industry-content">
      <CardHeader><CardTitle className="flex items-center gap-2"><Tag className="w-5 h-5 text-indigo-600" />Industry & Skills</CardTitle></CardHeader>
      <CardContent className="space-y-6">
        <div>
          <Label>Skills / Industry Tags</Label>
          <div className="flex gap-2 mt-2">
            <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag('skills'))} placeholder="Add a skill" data-testid="input-skill" />
            <Button variant="outline" onClick={() => addTag('skills')}><Plus className="w-4 h-4" /></Button>
          </div>
          {form.skills.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">{form.skills.map((s, i) => (
              <Badge key={i} variant="secondary" className="pl-3 pr-1 py-1">{s}<button onClick={() => updateField('skills', form.skills.filter((_, j) => j !== i))} className="ml-1.5 hover:bg-slate-300 rounded-full p-0.5"><X className="w-3 h-3" /></button></Badge>
            ))}</div>
          )}
        </div>
        {constants?.subjects && (
          <div>
            <Label>Subjects</Label>
            <div className="flex flex-wrap gap-2 mt-2 p-3 border rounded-lg bg-slate-50 max-h-40 overflow-y-auto">
              {constants.subjects.map(s => (
                <Badge key={s} variant={form.subjects_can_teach.includes(s) ? 'default' : 'outline'} className="cursor-pointer" onClick={() => toggleTag('subjects_can_teach', s)}>{s}</Badge>
              ))}
            </div>
          </div>
        )}
        {constants?.grade_levels && (
          <div>
            <Label>Grade Levels</Label>
            <div className="flex flex-wrap gap-2 mt-2">{constants.grade_levels.map(g => (
              <Badge key={g} variant={form.grade_levels_experience.includes(g) ? 'default' : 'outline'} className="cursor-pointer" onClick={() => toggleTag('grade_levels_experience', g)}>{g}</Badge>
            ))}</div>
          </div>
        )}
        {constants?.boards_curriculum && (
          <div>
            <Label>Board / Curriculum</Label>
            <div className="flex flex-wrap gap-2 mt-2">{constants.boards_curriculum.map(b => (
              <Badge key={b} variant={form.boards_experience.includes(b) ? 'default' : 'outline'} className="cursor-pointer" onClick={() => toggleTag('boards_experience', b)}>{b}</Badge>
            ))}</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const EducationSection = ({ form, addListEntry, removeListEntry, updateListEntry }) => (
  <Card data-testid="section-education-content">
    <CardHeader>
      <div className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2"><GraduationCap className="w-5 h-5 text-indigo-600" />Education</CardTitle>
        <Button variant="outline" size="sm" onClick={() => addListEntry('education', { institution: '', degree: '', field: '', start_year: '', end_year: '' })} data-testid="add-education-btn"><Plus className="w-3 h-3 mr-1" />Add</Button>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      {form.education.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No education added yet</p>}
      {form.education.map((edu, idx) => (
        <div key={idx} className="border rounded-lg p-4 space-y-3 relative">
          <button onClick={() => removeListEntry('education', idx)} className="absolute top-3 right-3 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="text-xs">Institution</Label><Input value={edu.institution || ''} onChange={(e) => updateListEntry('education', idx, 'institution', e.target.value)} className="mt-1" placeholder="DPS Bangalore" /></div>
            <div><Label className="text-xs">Degree</Label><Input value={edu.degree || ''} onChange={(e) => updateListEntry('education', idx, 'degree', e.target.value)} className="mt-1" placeholder="B.Ed" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">Field</Label><Input value={edu.field || ''} onChange={(e) => updateListEntry('education', idx, 'field', e.target.value)} className="mt-1" placeholder="Mathematics" /></div>
            <div><Label className="text-xs">Start Year</Label><Input value={edu.start_year || edu.year || ''} onChange={(e) => updateListEntry('education', idx, 'start_year', e.target.value)} className="mt-1" placeholder="2015" /></div>
            <div><Label className="text-xs">End Year</Label><Input value={edu.end_year || ''} onChange={(e) => updateListEntry('education', idx, 'end_year', e.target.value)} className="mt-1" placeholder="2019" /></div>
          </div>
        </div>
      ))}
    </CardContent>
  </Card>
);

const ProjectsSection = ({ form, addListEntry, removeListEntry, updateListEntry }) => (
  <Card data-testid="section-projects-content">
    <CardHeader>
      <div className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2"><FolderOpen className="w-5 h-5 text-indigo-600" />Projects</CardTitle>
        <Button variant="outline" size="sm" onClick={() => addListEntry('projects', { title: '', description: '', url: '' })} data-testid="add-project-btn"><Plus className="w-3 h-3 mr-1" />Add</Button>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      {form.projects.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No projects added yet</p>}
      {form.projects.map((proj, idx) => (
        <div key={idx} className="border rounded-lg p-4 space-y-3 relative">
          <button onClick={() => removeListEntry('projects', idx)} className="absolute top-3 right-3 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
          <div><Label className="text-xs">Title</Label><Input value={proj.title || ''} onChange={(e) => updateListEntry('projects', idx, 'title', e.target.value)} className="mt-1" /></div>
          <div><Label className="text-xs">Description</Label><Textarea value={proj.description || ''} onChange={(e) => updateListEntry('projects', idx, 'description', e.target.value)} className="mt-1" rows={2} /></div>
          <div><Label className="text-xs">URL (optional)</Label><Input value={proj.url || ''} onChange={(e) => updateListEntry('projects', idx, 'url', e.target.value)} className="mt-1" /></div>
        </div>
      ))}
    </CardContent>
  </Card>
);

const CertificationsSection = ({ form, addListEntry, removeListEntry, updateListEntry }) => (
  <Card data-testid="section-certifications-content">
    <CardHeader>
      <div className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2"><Award className="w-5 h-5 text-indigo-600" />Certifications</CardTitle>
        <Button variant="outline" size="sm" onClick={() => addListEntry('certifications', { name: '', issuer: '', year: '', score: '' })} data-testid="add-cert-btn"><Plus className="w-3 h-3 mr-1" />Add</Button>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      {form.certifications.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No certifications added yet. Add B.Ed, CTET, TET, etc.</p>}
      {form.certifications.map((cert, idx) => (
        <div key={idx} className="border rounded-lg p-4 relative">
          <button onClick={() => removeListEntry('certifications', idx)} className="absolute top-3 right-3 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
          <div className="grid md:grid-cols-4 gap-3">
            <div className="md:col-span-2"><Label className="text-xs">Name</Label><Input value={cert.name || ''} onChange={(e) => updateListEntry('certifications', idx, 'name', e.target.value)} className="mt-1" placeholder="B.Ed / CTET / TET" /></div>
            <div><Label className="text-xs">Year</Label><Input value={cert.year || ''} onChange={(e) => updateListEntry('certifications', idx, 'year', e.target.value)} className="mt-1" /></div>
            <div><Label className="text-xs">Score</Label><Input value={cert.score || ''} onChange={(e) => updateListEntry('certifications', idx, 'score', e.target.value)} className="mt-1" /></div>
          </div>
        </div>
      ))}
    </CardContent>
  </Card>
);

const ExperienceSection = ({ form, addListEntry, removeListEntry, updateListEntry }) => (
  <Card data-testid="section-experience-content">
    <CardHeader>
      <div className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2"><Briefcase className="w-5 h-5 text-indigo-600" />Experience</CardTitle>
        <Button variant="outline" size="sm" onClick={() => addListEntry('work_experience', { title: '', company: '', location: '', start_date: '', end_date: '', currently_working: false, description: '' })} data-testid="add-exp-btn"><Plus className="w-3 h-3 mr-1" />Add</Button>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      {form.work_experience.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No experience added yet</p>}
      {form.work_experience.map((exp, idx) => (
        <div key={idx} className="border rounded-lg p-4 space-y-3 relative">
          <button onClick={() => removeListEntry('work_experience', idx)} className="absolute top-3 right-3 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="text-xs">Title</Label><Input value={exp.title || ''} onChange={(e) => updateListEntry('work_experience', idx, 'title', e.target.value)} className="mt-1" placeholder="Senior Teacher" /></div>
            <div><Label className="text-xs">Company / School</Label><Input value={exp.company || ''} onChange={(e) => updateListEntry('work_experience', idx, 'company', e.target.value)} className="mt-1" /></div>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <div><Label className="text-xs">Location</Label><Input value={exp.location || ''} onChange={(e) => updateListEntry('work_experience', idx, 'location', e.target.value)} className="mt-1" /></div>
            <div><Label className="text-xs">Start Date</Label><Input value={exp.start_date || ''} onChange={(e) => updateListEntry('work_experience', idx, 'start_date', e.target.value)} className="mt-1" placeholder="Jan 2020" /></div>
            <div><Label className="text-xs">End Date</Label><Input value={exp.currently_working ? 'Present' : (exp.end_date || '')} onChange={(e) => updateListEntry('work_experience', idx, 'end_date', e.target.value)} disabled={exp.currently_working} className="mt-1" placeholder="Present" /></div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={exp.currently_working || false} onChange={(e) => updateListEntry('work_experience', idx, 'currently_working', e.target.checked)} className="rounded" id={`cw-${idx}`} />
            <Label htmlFor={`cw-${idx}`} className="text-sm cursor-pointer">I currently work here</Label>
          </div>
          <div><Label className="text-xs">Description</Label><Textarea value={exp.description || ''} onChange={(e) => updateListEntry('work_experience', idx, 'description', e.target.value)} className="mt-1" rows={2} /></div>
        </div>
      ))}
    </CardContent>
  </Card>
);

export default CandidateProfilePage;
